import { h, mount } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import { button, loading, toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { aiAvailableSync } from "@/services/ai.service";
import { getErrorCards, clearErrorCards, removeErrorCard } from "@/services/error-deck.service";
import { getMethodTrend, getSummary, getHistory } from "@/services/exercise-history.service";
import { currentStreak, syncStreak } from "@/services/streak.service";
import { aiTroubleCard } from "@/features/learn/aiHealth";
import { navigate, type RouteContext } from "@/app/router";
import type { Topic } from "@/types/models";

/**
 * Üben-Übersicht: /topic/:id/learn
 *
 * Die Seite ist nur noch ein Verteiler. Die eigentlichen Übungen laufen als
 * Vollbild-Seiten mit eigenem Design:
 *   /topic/:id/pfad            Lernpfad (Sequenzen, Lektionen, 9 Aufgabentypen)
 *   /topic/:id/karten          Karteikarten (Leitner, Zuordnen, Prüfung)
 *   /topic/:id/vortrag/recall  Active Recall als Vortrag (Spracherkennung)
 *   /topic/:id/vortrag/feynman Feynman-Vortrag für eine Zielgruppe
 *
 * Hier selbst liegen nur noch Probeklausur und Fehler-Deck – beide brauchen
 * keine eigene Vollbild-Insel. Was früher hier stand und warum es weg ist,
 * steht in GELÖSCHT.md.
 */

interface MethodDef {
  key: string;
  title: string;
  icon: IconName;
  desc: string;
  science: string;
  needsAi: boolean;
  route: (topicId: string) => string;
}

const METHODS: MethodDef[] = [
  {
    key: "pfad",
    title: "Lernpfad",
    icon: "target",
    desc: "Sequenzen und kurze Lektionen mit neun Aufgabentypen, AFB 1–3, Tipps und Streak.",
    science: "Verteiltes Üben mit steigender Anforderung (Spacing + Scaffolding).",
    needsAi: true,
    route: (id) => `/topic/${id}/pfad`,
  },
  {
    key: "karten",
    title: "Karteikarten",
    icon: "layers",
    desc: "Leitner-Fächer, Zuordnen und Prüfung – Karten selbst verwalten oder aus Notizen und KI übernehmen.",
    science: "Retrieval Practice mit wachsenden Wiederholungsabständen.",
    needsAi: false,
    route: (id) => `/topic/${id}/karten`,
  },
  {
    key: "recall",
    title: "Active Recall (Vortrag)",
    icon: "mic",
    desc: "Sag frei alles, was du weißt – die KI hört zu und vergleicht mit deinem Material.",
    science: "Testing-Effekt: freies Abrufen festigt stärker als Wiedererkennen.",
    needsAi: true,
    route: (id) => `/topic/${id}/vortrag/recall`,
  },
  {
    key: "feynman",
    title: "Feynman-Vortrag",
    icon: "lightbulb",
    desc: "Erkläre adressatengerecht – vom Grundschulkind bis zur Prüfungskommission. Mit Redezeit und Runden.",
    science: "Selbsterklärung deckt Verständnislücken auf (Elaboration).",
    needsAi: true,
    route: (id) => `/topic/${id}/vortrag/feynman`,
  },
  {
    key: "gespraech",
    title: "Gespräch",
    icon: "mic",
    desc: "Ein Charakter stellt dir Fragen zum Thema und reagiert – Ton und Laune passen sich an, wie gut die Antwort war.",
    science: "Elaboratives Interrogieren: Nachfragen vertiefen das Verständnis.",
    needsAi: true,
    route: (id) => `/topic/${id}/gespraech`,
  },
  {
    key: "klausur",
    title: "Probeklausur",
    icon: "pencil",
    desc: "Echtes Quellenmaterial aus dem Netz, passende Operatoren, Timer und Korrektur mit Randnotizen.",
    science: "Übung unter Prüfungsbedingungen (Transfer-Training).",
    needsAi: true,
    route: (id) => `/topic/${id}/klausur`,
  },
  {
    key: "fehler-deck",
    title: "Meine Fehler",
    icon: "alert-triangle",
    desc: "Alles, was du im Lernpfad falsch beantwortet hast, als Wiederholungs-Deck.",
    science: "Gezielte Fehleranalyse ist der stärkste einzelne Lernhebel.",
    needsAi: false,
    route: (id) => `/topic/${id}/learn/fehler-deck`,
  },
];

export async function LearnPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const method = ctx.params.method;

  let topic: Topic;
  try {
    topic = await topicsService.get(topicId);
  } catch (err) {
    return h("div", { class: "card card-pad field-error" }, pbErrorMessage(err));
  }

  if (!method) return overview(topic);
  if (method === "fehler-deck") return runner(topic, "Meine Fehler", errorDeckView);
  if (method === "klausur") { navigate(`/topic/${topicId}/klausur`); return h("div", null, loading()); }

  // Alte Lesezeichen (/learn/quiz, /learn/karten …) landen im Lernpfad statt
  // auf einer Fehlerseite – die Methoden sind dort aufgegangen.
  navigate(`/topic/${topicId}/learn`);
  return h("div", null, loading());
}

/* ================= Übersicht ================= */

function overview(topic: Topic): HTMLElement {
  void syncStreak();
  const streak = currentStreak();

  return h(
    "div",
    null,
    h(
      "div",
      { class: "topic-top" },
      button({ icon: "chevron-left", variant: "ghost", title: "Zurück", onClick: () => navigate(`/topic/${topic.id}`) }),
      h("div", { class: "t-headings" }, h("h1", null, "Üben"), h("div", { class: "topic-crumb" }, topic.title)),
      streak
        ? h("span", { class: "learn-streak", title: `${streak} Tage in Folge geübt` },
            h("span", { class: "row", html: icon("zap", 18) }), String(streak))
        : null
    ),

    aiAvailableSync() ? null : aiTroubleCard("Lernpfad, Vortrag und Probeklausur brauchen KI."),

    promptPdfCard(topic),

    h(
      "div",
      { class: "grid grid-auto", style: "margin-top:14px" },
      ...METHODS.map((m) => {
        const locked = m.needsAi && !aiAvailableSync();
        return h(
          "button",
          {
            class: "card card-pad lt-method" + (locked ? " locked" : ""),
            onclick: () => !locked && navigate(m.route(topic.id)),
          },
          h("div", { class: "row", style: "color:var(--primary)", html: icon(m.icon, 22) }),
          h("h3", { class: "mt-1" }, m.title),
          h("p", { class: "muted text-sm" }, m.desc),
          h("p", { class: "lt-science" }, m.science),
          locked ? h("span", { class: "badge" }, "KI nötig") : null
        );
      })
    ),

    progressPanel(topic)
  );
}

function promptPdfCard(topic: Topic): HTMLElement {
  const status = h("span", { class: "muted text-sm" });
  const btn = button({
    label: "Prompt-PDF exportieren",
    icon: "file",
    onClick: async () => {
      btn.disabled = true;
      try {
        const { exportPromptPdf } = await import("@/features/learn/promptPdf");
        await exportPromptPdf(topic, (message) => (status.textContent = message));
        status.textContent = "Fertig – PDF bei einer KI hochladen und loslegen.";
      } catch (err) {
        status.textContent = "";
        toast(err instanceof Error ? err.message : pbErrorMessage(err), "error");
      } finally {
        btn.disabled = false;
      }
    },
  });
  return h(
    "div",
    { class: "card card-pad prompt-pdf-card" },
    h("h3", null, "Mit anderen KIs lernen"),
    h(
      "p",
      { class: "muted text-sm" },
      "Ein PDF mit Fach, Prüfungstermin, einer Lern-Anleitung für die KI, deinen Mitschriften und allen Dokumenten – bei ChatGPT, Gemini & Co. hochladen."
    ),
    h("div", { class: "row", style: "gap:10px;flex-wrap:wrap;align-items:center" }, btn, status)
  );
}

/** Ermutigendes Fortschritts-Panel mit Mini-Verlaufsdiagramm. */
function progressPanel(topic: Topic): HTMLElement | null {
  const summary = getSummary(topic.id);
  if (!summary.runs) return null;
  const quizTrend = getMethodTrend(topic.id, "quiz").map((r) => r.pct);
  const series = quizTrend.length >= 2 ? quizTrend : getHistory(topic.id).slice().reverse().map((r) => r.pct);

  const impro = summary.improvement;
  const improText =
    summary.runs < 2
      ? "Dein Start – weiter so!"
      : impro > 0
        ? `📈 +${impro} Prozentpunkte besser als am Anfang!`
        : impro < 0
          ? "Dranbleiben – Wiederholung zahlt sich aus."
          : "Stabil – jetzt den nächsten Schritt!";

  return h(
    "div",
    { class: "card section", style: "margin-top:16px" },
    h("div", { class: "section-title" }, h("span", { class: "row", html: icon("bar-chart", 18) }), "Dein Fortschritt"),
    h(
      "div",
      { class: "prog-kpis" },
      progKpi(`${summary.runs}`, "Übungen"),
      progKpi(`${summary.avgPct}%`, "Schnitt"),
      progKpi(`${summary.bestPct}%`, "Bestwert"),
      progKpi(summary.lastPct !== null ? `${summary.lastPct}%` : "–", "Zuletzt")
    ),
    series.length >= 2 ? sparkline(series) : null,
    h("p", { class: "prog-improve" }, improText)
  );
}

function progKpi(value: string, label: string): HTMLElement {
  return h("div", { class: "prog-kpi" }, h("div", { class: "prog-kpi-val" }, value), h("div", { class: "prog-kpi-lbl" }, label));
}

/** Schlichtes SVG-Liniendiagramm der Trefferquoten (0–100 %). */
function sparkline(values: number[]): HTMLElement {
  const W = 100, H = 32, pad = 2;
  const n = values.length;
  const pts = values.map((v, i) => {
    const x = pad + (i / (n - 1)) * (W - 2 * pad);
    const y = H - pad - (v / 100) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const poly = pts.join(" ");
  const area = `${pad},${H - pad} ${poly} ${W - pad},${H - pad}`;
  const svg =
    `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="prog-spark">` +
    `<polygon points="${area}" class="prog-spark-area"/>` +
    `<polyline points="${poly}" class="prog-spark-line"/>` +
    pts.map((p) => { const [x, y] = p.split(","); return `<circle cx="${x}" cy="${y}" r="0.9" class="prog-spark-dot"/>`; }).join("") +
    `</svg>`;
  return h("div", { class: "prog-spark-wrap", html: svg });
}

/* ================= Rahmen für die zwei verbliebenen Methoden ================= */

function runner(topic: Topic, title: string, view: (topic: Topic) => HTMLElement): HTMLElement {
  return h(
    "div",
    null,
    h(
      "div",
      { class: "topic-top" },
      button({ icon: "chevron-left", variant: "ghost", title: "Zurück", onClick: () => navigate(`/topic/${topic.id}/learn`) }),
      h("div", { class: "t-headings" }, h("h1", null, title), h("div", { class: "topic-crumb" }, topic.title))
    ),
    view(topic)
  );
}

/* ================= Fehler-Deck ================= */

function errorDeckView(topic: Topic): HTMLElement {
  const wrap = h("div", null);

  function render(): void {
    const cards = getErrorCards(topic.id);
    if (!cards.length) {
      mount(wrap, h("div", { class: "card card-pad" },
        h("h3", { style: "text-align:center;margin:8px 0 4px" }, "Keine Fehler gespeichert"),
        h("p", { class: "muted", style: "text-align:center" },
          "Alles, was du im Lernpfad, in den Karteikarten, der Klausur oder im Vortrag falsch hattest, landet hier.")));
      return;
    }
    mount(wrap, h("div", null,
      h("div", { class: "lt-stats-bar", style: "margin-bottom:12px" },
        h("span", { class: "lt-stat-pill lt-stat-due" }, `${cards.length} offene Fehler`),
        h("span", { class: "spacer" }),
        button({ label: "Alle löschen", variant: "ghost", size: "sm",
          onClick: () => { clearErrorCards(topic.id); render(); } })),
      h("div", { class: "pf-errlist" }, ...cards.map((c) => {
        const row = h("div", { class: "pf-err" },
          h("div", { style: "flex:1;min-width:0" },
            h("div", { class: "q" }, c.question),
            h("div", { class: "a" }, c.answer)),
          h("button", {
            class: "again", type: "button", title: "Erledigt – aus der Liste nehmen",
            html: icon("refresh-cw", 18),
            onclick: () => { removeErrorCard(topic.id, c.question); row.classList.add("solved"); window.setTimeout(render, 250); },
          }));
        return row;
      }))));
  }

  render();
  return wrap;
}
